import React, { ChangeEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { stripDeviceIdPrefix } from '@controlant/utility-library/device-id';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { APP_NAME } from '../../AppConstants';
import warningSound from '../../assets/sci-fi-error-alert.wav';
import ErrorHandler from '../../components/ErrorHandler';
import Loader from '../../components/Loader';
import LoggerCheckTable from '../../components/LoggerCheckTable';
import ScanOrBoxDetailInputSection, { InputsRef } from '../../components/ScanOrBoxDetailInputSection';
import ScanOrBoxInfoHeader from '../../components/ScanOrBoxInfoHeader';
import { TOAST_TYPE_MAP, ToastCustom } from '../../components/ToastCustom';
import { toObjectQueryState } from '../../helpers/DataHelpers';
import {
    getCheckEndpointServiceErrModalContent,
    getClearBoxModalContent,
    getClearScanModalContent,
    getDevicesUsedInShipmentModalContent,
    getLoggerDidNotPassContent,
    getSingleCustomerModalContent
} from '../../helpers/ModalContentHelpers';
import { getCompleteBtnDataTipMessage, updateListElementWithNewCheckData } from '../../helpers/ScanningHelpers';
import { getStatisticsModalContent } from '../../helpers/ScanStatisticsHelpers';
import { isNonNullable, isNullable } from '../../helpers/TypingHelpers';
import { getCompleteScanOrBoxContent } from '../../modals/content/CompleteScanOrBox';
import { FlagAsDamagedModal } from '../../modals/FlagAsDamagedModal/FlagAsDamagedModal';
import GenericModal from '../../modals/GenericModal';
import ImportDevicesModal from '../../modals/ImportDevicesModal/ImportDevicesModal';
import VerifyLoggerLabelModal from '../../modals/VerifyLoggerLabelModal';
import { flagLoggerAsDamaged, getDamagedFlagsByLoggerId } from '../../services/Devices';
import { useValidateAndCompleteScanOrBox } from '../../services/hooks/useCompleteScanOrBox';
import { getBoxQueryKey, useGetBox } from '../../services/hooks/useGetBox';
import { useGetImportStatus } from '../../services/hooks/useGetImportStatus';
import { useGetLocations } from '../../services/hooks/useGetLocations';
import { getScanQueryKey, useGetScan } from '../../services/hooks/useGetScan';
import { useRunLoggerCheckMutation } from '../../services/hooks/useRunLoggerCheck';
import { DeleteChecks, deleteChecks } from '../../services/LoggerCheck';
import { useAllScanTypes } from '../../services/scan-types/queries';
import { GroupType } from '../../services/scan-types/schemas';
import { importIntoScanOrBox } from '../../services/Scans';
import { ImportInfo, ImportParams } from '../../types/Box';
import { ApiError } from '../../types/Error';
import type { FlagKey } from '../../types/Flag';
import { Location } from '../../types/Location';
import { Logger, LoggerCheckErrorRes, LoggerCheckResponse, LoggerId, LoggerListItem } from '../../types/Logger';
import { FlagAsDamagedModalData, GenericModalData, VerifyLabelModalData } from '../../types/Modal';
import {
    ImportScanItemRes,
    ImportScanPayload,
    ScanActionType,
    ScanItemType,
    ScanItemTypeEnum,
    ScanRes
} from '../../types/Scan';
import ScanCreateEditModal from '../ScanCreateEditModal';
import { ScanProps } from '../ScansHandler';
import {
    doesScanBelongToUserLocation,
    handleValidateAndSubmitError,
    handleValidateAndSubmitSuccess,
    invalidateGetScanOrBoxQuery,
    isAnotherUserScanning,
    isFailedLoggerAllowedForScanType,
    validateAndConstructImportPayload,
    validateLoggers
} from './ScanOrBoxDetail.utils';

import '../../styles/views/ScanOrBoxDetail.less';
import '../../styles/components/LoggerCheckTable.less';

/* Detail view for a scan or box within a scan */
const ScanOrBoxDetail = ({ currentUser }: ScanProps): ReactElement => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const { scanId, boxId } = useParams<{ scanId: string; boxId: string }>();
    const loggerListRef = useRef<Array<LoggerListItem>>([]);
    const searchRef = useRef<HTMLInputElement>(null);
    const inputSectionRef = useRef<InputsRef>(null);

    // Table and search state
    const [loggerList, setLoggerList] = useState<Array<LoggerListItem>>([]);
    const [searchByLoggerId, setSearchByLoggerId] = useState('');
    const [loggerMinLimitErr, setLoggerMinLimitErr] = useState(false);
    const [loggerMaxLimitErr, setLoggerMaxLimitErr] = useState(false);
    const [firstLoggerCheck, setFirstLoggerCheck] = useState<LoggerListItem>();
    const [isLoadingCheckForIds, setIsLoadingCheckForIds] = useState<Array<LoggerId>>([]);
    const [isLoadingDeleteForIds, setIsLoadingDeleteForIds] = useState<Array<LoggerId>>([]);
    const [failedCheckCountOnOutboundGroupSubmit, setFailedCheckCountOnOutboundGroupSubmit] = useState(0);
    const [failureSound, setFailureSound] = useState<HTMLAudioElement>();

    // Modal state
    const [modalData, setModalData] = useState<GenericModalData | null>(null);
    const [flagAsDamagedModalData, setFlagAsDamagedModalData] = useState<FlagAsDamagedModalData>(null);
    const [showCreateEditModal, setShowCreateEditModal] = useState(false);
    const [showImportDevicesModal, setShowImportDevicesModal] = useState(false);
    const [verifyLabelModalData, setVerifyLabelModalData] = useState<VerifyLabelModalData | null>(null);
    const [failureModalQueue, setFailureModalQueue] = useState<Array<Logger>>([]);
    const [verifyLabelModalQueue, setVerifyLabelModalQueue] = useState<Array<Logger>>([]);
    const [singleCustomerModalQueue, setSingleCustomerModalQueue] = useState<
        Array<{ loggerId: LoggerId; customerName?: string; loggerToRemove?: Logger }>
    >([]);
    const [devicesUsedInShipmentModalQueue, setDevicesUsedInShipmentModalQueue] = useState<
        Array<{ loggerIds: Array<LoggerId> }>
    >([]);
    const [checkErrorModalQueue, setCheckErrorModalQueue] = useState<
        Array<{ errorMessage: string | undefined; loggerId: LoggerId; requestId: string | undefined }>
    >([]);

    // Import loggers state
    const [isImportOngoing, setIsImportOngoing] = useState(false);
    const [scanEntityToImport, setScanEntityToImport] = useState<{
        importEntityId: string;
        importItemType: ScanItemType;
    }>();

    const { data: scanRes, isLoading: isLoadingScan } = useGetScan({ scanId: scanId, onError: ErrorHandler });
    const { data: boxRes, isLoading: isLoadingBox } = useGetBox({ boxId: boxId, onError: ErrorHandler });
    const scanOrBoxRes = boxRes ?? scanRes;

    const { locations } = useGetLocations();
    const scanTypes = useAllScanTypes({ params: { page: 1, show: 100 } });

    const { mutate: deleteCheckMutation } = useMutation<void, AxiosError, DeleteChecks>({
        mutationFn: deleteChecks,
        onSuccess: (_, variables) => {
            handleRemoveLoggersFromList(variables.payload.loggerIds.map((loggerId) => loggerId));
            invalidateGetScanOrBoxQuery(queryClient, scanId, boxId);
        },
        onError: (err) => ErrorHandler(err, null, 'Deleting loggers failed'),
        onSettled: (_, __, variables) =>
            setIsLoadingDeleteForIds((prevState) => prevState.filter((id) => id !== variables.payload.loggerIds[0]))
    });

    const { mutate: deleteAllChecksMutation, isPending: isPendingDeletingAllChecks } = useMutation<
        void,
        AxiosError,
        DeleteChecks
    >({
        mutationFn: deleteChecks,
        onSuccess: () => {
            invalidateGetScanOrBoxQuery(queryClient, scanId, boxId);
            setLoggerList([]);
            setFirstLoggerCheck(undefined);
            setFailedCheckCountOnOutboundGroupSubmit(0);
            inputSectionRef.current?.clearLimits();
        },
        onError: (err) => ErrorHandler(err, null, 'Deleting all logger checks failed')
    });

    const { mutate: runLoggerCheck, isPending: isLoggerCheckPending } = useRunLoggerCheckMutation({
        onSuccess: (res: LoggerCheckResponse) => {
            const loggers = Object.values(res);
            handleLoggerCheckSuccessRes(loggers, res);
        },
        onError: (error: AxiosError, variables) => {
            invalidateGetScanOrBoxQuery(queryClient, scanId, boxId);
            const loggerIds = variables.payload.loggerIds;
            handleRemoveLoggersFromList(loggerIds);

            const customError = error?.response?.data as LoggerCheckErrorRes;
            const errDetails = customError?.details;

            if (errDetails?.errorCode === 'MIXED_CUSTOMERS_IN_OUTBOUND_SCAN') {
                const singleCustomerModalList: { loggerId: LoggerId; customerName: string | undefined }[] = [];
                loggerIds.forEach((loggerId) => {
                    const customerName = errDetails?.data?.failingLoggers?.[loggerId]?.name;
                    singleCustomerModalList.push({ loggerId: loggerId, customerName: customerName });
                });
                setSingleCustomerModalQueue((prevState) => [...prevState, ...singleCustomerModalList]);
            } else {
                if (!error) return;

                setCheckErrorModalQueue((prevState) => [
                    ...prevState,
                    {
                        errorMessage: customError.message,
                        loggerId: loggerIds[0],
                        requestId: errDetails?.request_id
                    }
                ]);
            }
        },
        onSettled: (_, __, variables) =>
            setIsLoadingCheckForIds((prevState) => prevState.filter((id) => id !== variables.payload.loggerIds[0]))
    });

    const { mutate: validateAndSubmitMutation, isPending: isPendingCompletion } = useValidateAndCompleteScanOrBox(
        (submitRes) => handleValidateAndSubmitSuccess(submitRes, setModalData, scanOrBoxRes, navigate),
        (error) => handleValidateAndSubmitError(error, setDevicesUsedInShipmentModalQueue)
    );

    const boxToImportResults = useGetBox({
        boxId:
            scanEntityToImport?.importItemType === ScanItemTypeEnum.BOX ? scanEntityToImport?.importEntityId : undefined
    });

    // When importing a box, fetch its parent scan via box.scanId, needed for validating and summary info in the Import Modal
    const scanIdToImport =
        scanEntityToImport?.importItemType === ScanItemTypeEnum.BOX
            ? boxToImportResults?.data?.scanId?.toString()
            : scanEntityToImport?.importEntityId;
    const scanToImportResults = useGetScan({ scanId: scanIdToImport });

    const loggerIdToMark = flagAsDamagedModalData?.type === 'flag' ? flagAsDamagedModalData.logger.loggerId : undefined;

    const { data: damagedFlags, isLoading: isLoadingDamagedFlags } = useQuery({
        queryKey: ['logger-damaged-flags', loggerIdToMark],
        queryFn: () => {
            if (!loggerIdToMark) return Promise.resolve(null);
            return getDamagedFlagsByLoggerId(loggerIdToMark);
        },
        retry: false,
        staleTime: 0,
        enabled: flagAsDamagedModalData?.type === 'flag'
    });

    const importIntoScanOrBoxMutation: UseMutationResult<ImportScanItemRes, ApiError, ImportParams> = useMutation({
        mutationFn: importIntoScanOrBox,
        onSuccess: () => {
            setIsImportOngoing(true);
            setShowImportDevicesModal(false);
        }
    });

    // We check if an import is in progress, if so, we poll the status of the import
    const isScanItemInProgress = scanOrBoxRes?.statusName === 'IN_PROGRESS';
    const isScanItemComplete = scanOrBoxRes?.statusName === 'COMPLETE';

    const { importJobStatus } = useGetImportStatus({
        scanId: scanId,
        boxId: boxId,
        shouldStartOnMount: isScanItemInProgress || isImportOngoing,
        hasManualTrigger: isImportOngoing,
        onError: () => setIsImportOngoing(false),
        onTimeout: () => {
            ToastCustom('The import job timed out!', TOAST_TYPE_MAP.ERROR);
            setIsImportOngoing(false);
        },
        onStatusComplete: () => {
            setIsImportOngoing(false);
            handleResetImportEntities();
        }
    });

    // This effect keeps the isImportOngoing state in sync with the current importJobStatus,
    // so that the loading indicator reflects the correct import progress. Also needed on mount to check the importJobStatus.
    useEffect(() => {
        if (importJobStatus === 'PENDING' || importJobStatus === 'PROCESSING') {
            setIsImportOngoing(true);
        }
    }, [importJobStatus]);

    const flagAsDamagedMutation = useMutation<unknown, Error, { loggerId: LoggerId; flags: Array<FlagKey> }>({
        mutationFn: (variables) => flagLoggerAsDamaged(variables.loggerId, variables.flags),
        onSuccess: (_, variables) => {
            ToastCustom(`Logger ${variables.loggerId} was flagged as damaged`, TOAST_TYPE_MAP.SUCCESS);
            runLoggerCheckQuery([variables.loggerId]);
        },
        onError: (_, variables) => {
            ToastCustom(`Failed to flag logger ${variables.loggerId} as damaged`, TOAST_TYPE_MAP.ERROR);
        }
    });

    const isFailedChecksAllowedForScanType = useMemo(() => {
        return isFailedLoggerAllowedForScanType(scanOrBoxRes, scanTypes?.data?.items);
    }, [scanOrBoxRes, scanTypes?.data?.items]);

    useEffect(() => {
        document.title = `Scan ${scanId}`;
        setFailureSound(new Audio(warningSound));
        return function cleanUp() {
            document.title = APP_NAME;
        };
    }, []);

    function runLoggerCheckQuery(loggerIds: Array<LoggerId>) {
        setIsLoadingCheckForIds((prevState) => [...prevState, ...loggerIds]);
        runLoggerCheck({
            boxId: boxId,
            scanId: scanId,
            queryParams: { scan_source: scanOrBoxRes?.scanTypeGroupType.toLocaleLowerCase() ?? '' },
            payload: { loggerIds, allowUnapprovedCalibrations: false, useCachedResults: false }
        });
    }

    // closeMe() modal callback needs this ref to get up-to-date value when reading the state variable loggerList,
    // because the callback will have an old reference to the state variable
    loggerListRef.current = loggerList;

    useEffect(() => {
        if (!scanOrBoxRes) return;

        // We wait until scanTypes have been fetches successfully, since we need to know if failed loggers are allowed according to scan type
        if (scanTypes.isSuccess) {
            // We have a non-box scan so we can validate the loggers
            const { successLoggers, failedLoggers } = validateLoggers(scanOrBoxRes, isFailedChecksAllowedForScanType);
            setLoggerList([...failedLoggers, ...successLoggers]);

            if (!isFailedChecksAllowedForScanType) {
                setFailedCheckCountOnOutboundGroupSubmit(failedLoggers.length);
            }
        }
    }, [scanOrBoxRes, scanTypes.isSuccess, failedCheckCountOnOutboundGroupSubmit]);

    useEffect(() => {
        if (!firstLoggerCheck && loggerList.length) {
            setFirstLoggerCheck([...loggerList].reverse().find((logger) => logger.masterStatus !== 'pending'));
        } else if (!loggerList.length) {
            setFirstLoggerCheck(undefined);
        }
    }, [loggerList]);

    useEffect(() => {
        const isModalPending =
            failureModalQueue.length ||
            verifyLabelModalQueue.length ||
            singleCustomerModalQueue.length ||
            checkErrorModalQueue.length ||
            devicesUsedInShipmentModalQueue.length;

        const isModalOpen = !!(modalData || verifyLabelModalData);

        if (!isModalOpen && isModalPending) {
            setTimeout(() => {
                displayNextModal();
            }, 50);
        }
    }, [
        modalData,
        verifyLabelModalData,
        failureModalQueue,
        verifyLabelModalQueue,
        singleCustomerModalQueue,
        checkErrorModalQueue,
        devicesUsedInShipmentModalQueue
    ]);

    const handleRemoveLoggerApiCall = (loggersToRemove: Array<LoggerListItem>) => {
        if (scanId) {
            setIsLoadingDeleteForIds((prevState) => [
                ...prevState,
                ...loggersToRemove.map((logger) => logger.loggerId)
            ]);
            deleteCheckMutation({
                scanId: scanId,
                boxId: boxId,
                payload: {
                    loggerIds: loggersToRemove.map((logger) => logger.loggerId),
                    useCachedResults: false
                }
            });
        }
    };

    const handleRemoveLoggersFromList = (loggerIdsToRemove: Array<LoggerId>) => {
        const listCopy = loggerListRef.current.filter((logger) => !loggerIdsToRemove.includes(logger.loggerId));
        let firstLoggerRemoved = false;

        loggerIdsToRemove.forEach((loggerIdToRemove) => {
            if (firstLoggerCheck?.loggerId === loggerIdToRemove) {
                firstLoggerRemoved = true;
            }
            if (!isFailedChecksAllowedForScanType) {
                setFailedCheckCountOnOutboundGroupSubmit((prevState) => prevState - 1);
            }
        });
        setLoggerList(listCopy);

        if (firstLoggerRemoved) {
            setFirstLoggerCheck([...listCopy].reverse().find((logger) => logger.masterStatus !== 'pending'));
        }
    };

    const displayNextModal = () => {
        if (checkErrorModalQueue && checkErrorModalQueue.length > 0) {
            const errorResponse = checkErrorModalQueue.shift();

            if (errorResponse) {
                setModalData({
                    ...getCheckEndpointServiceErrModalContent(errorResponse),
                    closeMe: () => {
                        setModalData(null);
                    }
                });
            }
        } else if (singleCustomerModalQueue && singleCustomerModalQueue.length > 0) {
            const loggerCheck = singleCustomerModalQueue.shift();
            if (loggerCheck) {
                setModalData({
                    ...getSingleCustomerModalContent(loggerCheck.loggerId, loggerCheck.customerName),
                    closeMe: () => {
                        setModalData(null);
                        if (isNonNullable(loggerCheck.loggerToRemove)) {
                            handleRemoveLoggerApiCall([loggerCheck.loggerToRemove]);
                        }
                    }
                });
            }
        } else if (verifyLabelModalQueue && verifyLabelModalQueue.length > 0) {
            const loggerCheck = verifyLabelModalQueue.shift();
            if (loggerCheck) {
                setVerifyLabelModalData({
                    logger: loggerCheck,
                    action: (logger, actionType) => {
                        if (actionType === 'REMOVE') {
                            handleRemoveLoggerApiCall([logger]);
                        } else {
                            setLoggerList((prevState) => updateListElementWithNewCheckData([logger], prevState));
                        }
                    },
                    closeMe: () => setVerifyLabelModalData(null)
                });
            }
        } else if (failureModalQueue && failureModalQueue.length > 0) {
            const loggerCheck = failureModalQueue.shift();
            if (loggerCheck) {
                setModalData({
                    ...getLoggerDidNotPassContent(loggerCheck),
                    closeMe: () => {
                        setModalData(null);
                        handleRemoveLoggerApiCall([loggerCheck]);
                    }
                });
                failureSound?.play();
            }
        } else if (devicesUsedInShipmentModalQueue && devicesUsedInShipmentModalQueue.length > 0) {
            const loggersCheck = devicesUsedInShipmentModalQueue.shift();
            if (loggersCheck) {
                setModalData({
                    ...getDevicesUsedInShipmentModalContent(loggersCheck.loggerIds),
                    closeMe: () => {
                        setModalData(null);
                    }
                });
            }
        }
    };

    const clearScanOrBox = () => {
        if (scanId && loggerList?.length) {
            deleteAllChecksMutation({
                scanId: scanId,
                boxId: boxId,
                payload: { loggerIds: loggerList.map((logger) => logger.loggerId) }
            });
        }
    };

    const handleLoggerCheckValidation = (checks: Array<Logger>) => {
        if (checks.length == 0 || !scanOrBoxRes) {
            return;
        }

        // If a scan type group allows failed checks we add it straight to the list
        if (isFailedChecksAllowedForScanType) {
            setLoggerList((prevState) => updateListElementWithNewCheckData(checks, prevState));
            return;
        }

        // Otherwise, we run validation and add to appropriate modal queues
        const failureModalList: Logger[] = [];
        const verifyLabelModalList: Logger[] = [];
        const newCheckDataList: Logger[] = [];

        checks.forEach((check) => {
            if (check.masterStatus === 'failed') {
                failureModalList.push(check);
            } else if (check.verifyLoggerId) {
                verifyLabelModalList.push(check);
            } else {
                newCheckDataList.push(check);
            }
        });
        if (failureModalList.length > 0) {
            setFailureModalQueue((prevState) => [...prevState, ...failureModalList]);
        }
        if (verifyLabelModalList.length > 0) {
            setVerifyLabelModalQueue((prevState) => [...prevState, ...verifyLabelModalList]);
        }
        if (newCheckDataList.length > 0) {
            setLoggerList((prevState) => updateListElementWithNewCheckData(newCheckDataList, prevState));
        }
    };

    const handleLoggerCheckSuccessRes = (loggers: Array<LoggerListItem>, checkRes: LoggerCheckResponse) => {
        if (loggers.length == 0) {
            return;
        }

        const loggerCheck: Array<Logger> = [];

        for (const id of loggers.map((logger) => logger.loggerId)) {
            if (id in checkRes) {
                loggerCheck.push(checkRes[id]);
            }
        }
        handleLoggerCheckValidation(loggerCheck);
    };

    const handleScanAction = (_scanRes: ScanRes, event: ScanActionType, _scanBelongsToUserLocation: boolean) => {
        switch (event) {
            case 'EDIT':
                if (!_scanBelongsToUserLocation) {
                    ToastCustom('This scan/box does not belong to your location', TOAST_TYPE_MAP.ERROR);
                } else if (scanOrBoxRes && _scanBelongsToUserLocation) {
                    setShowCreateEditModal(true);
                }
                break;
            case 'COMPLETE':
                setModalData({
                    ...getCompleteScanOrBoxContent({
                        scan: _scanRes,
                        box: boxRes,
                        inspectLoggersCount: loggerList.filter((logger) => logger.masterStatus === 'inspect').length,
                        scannedLoggersCount: loggerList.length,
                        pendingLoggersCount: loggerList.filter((logger) => logger.masterStatus === 'pending').length,
                        warnAboutInspect: scanOrBoxRes?.scanTypeGroupType === GroupType.OUTBOUND
                    }),
                    confirmAction: () => validateAndSubmitMutation({ scanId: scanId, boxId: boxId }),
                    closeMe: () => setModalData(null)
                });
                break;
            case 'STATISTICS':
                setModalData({
                    ...getStatisticsModalContent(loggerList, Boolean(boxId)),
                    closeMe: () => setModalData(null)
                });
                break;
            case 'CLEAR':
                if (boxId) {
                    setModalData({
                        ...getClearBoxModalContent(),
                        confirmAction: () => clearScanOrBox(),
                        closeMe: () => setModalData(null)
                    });
                } else {
                    setModalData({
                        ...getClearScanModalContent(),
                        confirmAction: () => clearScanOrBox(),
                        closeMe: () => setModalData(null)
                    });
                }
                break;
            case 'IMPORT':
                if (scanOrBoxRes?.scanTypeGroupType === 'OUTBOUND') {
                    ToastCustom('Import not available for outbound scan types', TOAST_TYPE_MAP.ERROR);
                } else {
                    setIsImportOngoing(false);
                    setShowImportDevicesModal(true);
                }
                break;
            default:
                break;
        }
    };

    const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const loggerId = stripDeviceIdPrefix(event.target.value);
        setSearchByLoggerId(loggerId);
    };

    const handleResetImportEntities = () => {
        setShowImportDevicesModal(false);
        setScanEntityToImport(undefined);

        importIntoScanOrBoxMutation.reset();

        const entityIdToRemove = scanEntityToImport?.importEntityId;
        const isBox = scanEntityToImport?.importItemType === ScanItemTypeEnum.BOX;
        queryClient.removeQueries({
            queryKey: isBox ? getBoxQueryKey(entityIdToRemove) : getScanQueryKey(entityIdToRemove)
        });

        // If we are importing a box, we remove the parent scan as well, since it's used for validation in the import modal
        if (isBox) {
            queryClient.removeQueries({ queryKey: getScanQueryKey(scanIdToImport) });
        }
    };

    const handleImportScanEntity = (importInfo: ImportInfo, isForceEnabled: boolean | undefined) => {
        let payload: ImportScanPayload;

        try {
            payload = validateAndConstructImportPayload(importInfo, isForceEnabled);
        } catch (e) {
            if (e instanceof Error) {
                ToastCustom(e.message, TOAST_TYPE_MAP.ERROR);
            } else {
                ToastCustom('An unknown error occurred during import', TOAST_TYPE_MAP.ERROR);
            }
            setIsImportOngoing(false);
            return;
        }

        importIntoScanOrBoxMutation.mutate({ payload, targetScanId: scanId, targetBoxId: boxId });
    };

    const isLoadingScanItem = isLoadingBox || isLoadingScan;
    const isBatchActionLoading = isPendingCompletion || isPendingDeletingAllChecks || isImportOngoing;

    const scanBelongsToUserLocation = doesScanBelongToUserLocation(scanRes, currentUser);
    const isAnotherUserScanningVal = isAnotherUserScanning(scanOrBoxRes, currentUser);
    const canUserModifyScan =
        !isScanItemComplete &&
        !isAnotherUserScanningVal &&
        scanBelongsToUserLocation &&
        isNullable(scanOrBoxRes?.deletedTime);
    const isLoggerCountNotWithinLimit = loggerMinLimitErr || loggerMaxLimitErr;

    // True, when scan or box data is retrieved and not loading, with box data required if boxId exists.
    // scanRes is explicitly checked because it’s passed to child components.
    const scanDataIsLoaded =
        isNonNullable(scanOrBoxRes) && scanRes && !isLoadingScan && !isLoadingBox && ((boxId && boxRes) || !boxId);

    return (
        <div className="scan-container">
            <Loader size={20} loading={isLoadingScanItem} overComponent={true} />

            {scanDataIsLoaded && (
                <>
                    <ScanOrBoxInfoHeader
                        scanItem={scanOrBoxRes}
                        scanItemMeta={{
                            scanId: scanRes.id,
                            destinationName: scanRes.destinationName,
                            trackingId: scanRes.trackingId,
                            salesOrderId: scanRes.salesOrderId
                        }}
                        currentUser={currentUser}
                        onAction={(action) => handleScanAction(scanRes, action, scanBelongsToUserLocation)}
                        buttonsState={{
                            COMPLETE: {
                                isDisabled:
                                    !canUserModifyScan ||
                                    !loggerList.length ||
                                    isBatchActionLoading ||
                                    isLoggerCheckPending ||
                                    isLoggerCountNotWithinLimit ||
                                    Boolean(failedCheckCountOnOutboundGroupSubmit),
                                isLoading: isPendingCompletion,
                                isVisible:
                                    isScanItemInProgress &&
                                    !scanOrBoxRes?.deletedByUsername &&
                                    !isAnotherUserScanningVal,
                                tooltipMsg: getCompleteBtnDataTipMessage(
                                    scanBelongsToUserLocation,
                                    Boolean(failedCheckCountOnOutboundGroupSubmit),
                                    isLoggerCountNotWithinLimit,
                                    loggerList.length
                                )
                            },
                            ACTIONS: {
                                isDisabled: isImportOngoing,
                                isVisible: isNullable(scanOrBoxRes?.deletedByUsername)
                            }
                        }}
                    />

                    <ScanOrBoxDetailInputSection
                        ref={inputSectionRef}
                        scanOrBox={scanOrBoxRes}
                        currentUser={currentUser}
                        loggerListState={{
                            loggerList,
                            setLoggerList,
                            isLoggerCheckPending
                        }}
                        scanTypesResData={toObjectQueryState(scanTypes)}
                        isBatchActionLoading={isBatchActionLoading}
                        runLoggerCheckQuery={runLoggerCheckQuery}
                        loggerLimitState={{
                            setLoggerMinLimitErr,
                            setLoggerMaxLimitErr
                        }}
                        searchState={{
                            searchByLoggerId,
                            setSearchByLoggerId,
                            onSearchChange,
                            searchRef
                        }}
                    />

                    <LoggerCheckTable
                        scanItemRes={scanOrBoxRes}
                        loggerList={loggerList}
                        searchByLoggerId={searchByLoggerId}
                        currentUser={currentUser}
                        failedLoggerCheckCount={failedCheckCountOnOutboundGroupSubmit}
                        loadingState={{
                            isLoadingScanItem,
                            isPendingDeletingAllChecks,
                            isLoadingCheckForIds,
                            isLoadingDeleteForIds,
                            isImportOngoing
                        }}
                        setFlagAsDamagedModalData={setFlagAsDamagedModalData}
                        handleRemoveLoggerApiCall={handleRemoveLoggerApiCall}
                    />
                </>
            )}

            <GenericModal modalData={modalData} />
            <VerifyLoggerLabelModal modalData={verifyLabelModalData} />

            {showCreateEditModal && (
                <ScanCreateEditModal
                    currentUser={currentUser}
                    scanToEdit={scanRes}
                    destinations={locations?.map((location: Location) => location.name) ?? []}
                    closeMe={() => {
                        setShowCreateEditModal(false);
                    }}
                />
            )}

            {showImportDevicesModal && (
                <ImportDevicesModal
                    closeMe={() => handleResetImportEntities()}
                    fetchImportEntityToValidate={(importId, importType) => {
                        setScanEntityToImport({ importEntityId: importId, importItemType: importType });
                    }}
                    scanToImportRes={toObjectQueryState(scanToImportResults)}
                    boxToImportRes={toObjectQueryState(boxToImportResults)}
                    isPendingImport={importIntoScanOrBoxMutation.isPending}
                    handleImport={handleImportScanEntity}
                    importErrRes={importIntoScanOrBoxMutation.error}
                    currentUser={currentUser}
                />
            )}

            {flagAsDamagedModalData?.type === 'flag' && (
                <FlagAsDamagedModal
                    loggerId={flagAsDamagedModalData.logger.loggerId}
                    flags={(damagedFlags?.flags ?? []).map((flag) => {
                        const isActive = flag.loggerIds.includes(flagAsDamagedModalData.logger.loggerId);

                        return {
                            key: flag.key,
                            active: isActive,
                            // If the flag is already active, we want to disable it since we don't support removing flags
                            disabled: isActive
                        };
                    })}
                    onClose={() => setFlagAsDamagedModalData(null)}
                    onSubmit={(flags) => {
                        const loggerIdToMark = flagAsDamagedModalData.logger.loggerId;

                        flagAsDamagedMutation.mutate({
                            loggerId: loggerIdToMark,
                            flags: flags
                        });
                        setFlagAsDamagedModalData(null);
                    }}
                    isLoading={isLoadingDamagedFlags}
                />
            )}
        </div>
    );
};

export default ScanOrBoxDetail;
